import {
  logger,
  DeferredPromise
} from '../../utils'

import ApiManager, {
  prepareRequest
} from '../../api/request'

import Bus from '../../bus'

import * as subscriptionTopics from './subscription.topics'
import { isEmpty, isNumber } from 'lodash'

// export class SubscribtionError extends Error {
//   name = 'WebSocketSubscribeError'
// }

// Autorization subscriptions
const AUTH_SUBSCRIPTION_KEYS = [
  {
    notificationTopic: subscriptionTopics.AUTHORIZED_CLIENT_UPDATES,
    id: ''
  }
]

class SubscribtionManager {
  constructor () {
    this.sessionId = null
    this.authorizationPromise = new DeferredPromise()
    // Storing session id once socket session resolving
    Bus.on(Bus.events.socket.session, payload => this.setSession(payload.id))
    // Once connection open
    Bus.on(Bus.events.socket.open, () => {
      // Adding authorization subscription hander
      // Once authorization will be resolved
      this.authorizationPromise.then(this.subscribeForAuthenticationEvents)
    })

    // Once connection closed
    Bus.on(Bus.events.socket.closed, () => {
      // Removing storaged session once connection closed
      this.setSession(null)

      // Reinitializing authorization promise
      this.authorizationPromise = new DeferredPromise()

      // Resolving if token still present
      const authorizationToken = ApiManager.getAuthorizationToken()
      if (authorizationToken) {
        this.authorizationPromise.resolve(authorizationToken)
      }
    })

    Bus.on(Bus.events.socket.resubscribe, () => {
      if (isEmpty(this.activeSubscriptions)) {
        return
      }

      const subscriptionKeys = Object.keys(this.activeSubscriptions)
        .reduce(
          (acc, activeSubscriptionKey) => {
            const [notificationTopic, id] = activeSubscriptionKey.split('/')

            acc.push({
              notificationTopic,
              id
            })

            return acc
          },
          []
        )

      Bus.emit(Bus.events.socket.subscribe, {
        subscriptionKeys
      })
    })

    /*
      Handler for resolving token once authorization completed
    */
    const handleAuthorizationResolve = () => {
      const authorizationToken = ApiManager.getAuthorizationToken()

      this.authorizationPromise.resolve(authorizationToken)
    }
    Bus.on(Bus.events.auth.resolved, handleAuthorizationResolve)
    Bus.on(Bus.events.auth.login, handleAuthorizationResolve)

    // Should be first handler in logout event
    Bus.prependListener(Bus.events.auth.logoutSuccess, () => {
      const authorizationToken = ApiManager.getAuthorizationToken()

      if (!authorizationToken) {
        logger.debug('[subscription]', 'unable to remove auth handlers; auth token not found')
        return
      }

      // Unsubscribing from authentication events
      this.unsubscribeFromAuthenticationEvents(authorizationToken)

      // Reinitializing authorization promise
      this.authorizationPromise = new DeferredPromise()
      // Adding handlers for subscribing once authorization token will be resolved
      this.authorizationPromise.then(this.subscribeForAuthenticationEvents)
    })

    this.activeSubscriptions = {}
  }

  connect = () => {
    logger.debug('[subscription manager]', 'connected to application lifecycle')
  }

  setSession (sessionId) {
    if (sessionId) {
      logger.debug('[subscription]', `socket session initialized ${sessionId}`)
    }

    this.sessionId = sessionId
  }

  getSession () {
    return this.sessionId
  }

  subscribeForAuthenticationEvents (authorizationToken) {
    logger.debug('[subscription]', 'handling authorization')

    Bus.emit(Bus.events.socket.subscribe, {
      subscriptionKeys: AUTH_SUBSCRIPTION_KEYS,
      token: `Bearer ${authorizationToken}`
    })
  }

  unsubscribeFromAuthenticationEvents (authorizationToken) {
    if (!authorizationToken) {
      logger.debug('[subscription]', 'unable to remove auth handlers; auth token not found')
      return
    }

    Bus.emit(Bus.events.socket.unsubscribe, {
      subscriptionKeys: AUTH_SUBSCRIPTION_KEYS,
      token: `Bearer ${authorizationToken}`
    })
  }

  getCurrentSubscriptions () {
    const sessionId = this.getSession()

    const request = prepareRequest({
      method: 'GET',
      url: 'site-notifier/subscription',
      query: {
        sessionId
      }
    })

    return request()
  }

  /*
    Subscribe on topics
  */
  notifierSubscribe (subscribeOptions) {
    const subscriptionKeys = subscribeOptions.reduce(
      (acc, subscribtion) => {
        const { topic: notificationTopic, key: id } = subscribtion
        const subscriptionCount = this.getActiveSubscriptionCount(
          notificationTopic,
          id
        )

        // If counter 0 or not exist
        // Adding to subscriptions
        if (!subscriptionCount) {
          acc.push({
            notificationTopic,
            id
          })
        }

        this.incrementSubscribtionCounter(notificationTopic, id)

        return acc
      },
      []
    )

    if (isEmpty(subscriptionKeys)) {
      return
    }

    Bus.emit(Bus.events.socket.subscribe, {
      subscriptionKeys
    })
  }

  /*
    Unsubscribe from topics
  */
  notifierUnsubscribe (subscribeOptions) {
    const subscriptionKeys = subscribeOptions.reduce(
      (acc, subscribtion) => {
        const { topic: notificationTopic, key: id } = subscribtion
        const subscriptionCount = this.getActiveSubscriptionCount(
          notificationTopic,
          id
        )

        // If counter exist and <2
        // Adding topics for unsubscription
        if (subscriptionCount && (subscriptionCount < 2)) {
          acc.push({
            notificationTopic,
            id
          })
        }

        this.decrementSubscriptionCounter(notificationTopic, id)

        return acc
      },
      []
    )

    if (isEmpty(subscriptionKeys)) {
      return
    }

    Bus.emit(Bus.events.socket.unsubscribe, {
      subscriptionKeys
    })
  }

  getActiveSubscriptionCount (topic, key) {
    const subscribtionId = `${topic}/${key}`
    return this.activeSubscriptions[subscribtionId]
  }

  incrementSubscribtionCounter (topic, key) {
    const subscribtionId = `${topic}/${key}`

    if (isNumber(this.activeSubscriptions[subscribtionId]) && (this.activeSubscriptions[subscribtionId] > 0)) {
      this.activeSubscriptions[subscribtionId]++
    } else {
      this.activeSubscriptions[subscribtionId] = 1
    }
  }

  decrementSubscriptionCounter (topic, key) {
    const subscribtionId = `${topic}/${key}`

    if (isNumber(this.activeSubscriptions[subscribtionId]) && (this.activeSubscriptions[subscribtionId] > 0)) {
      this.activeSubscriptions[subscribtionId]--

      // Check if we need cleaning after decrementing counter
      if (this.activeSubscriptions[subscribtionId] <= 0) {
        delete this.activeSubscriptions[subscribtionId]
      }
    }
  }
}

const WSSubscribtionManager = new SubscribtionManager()

WSSubscribtionManager.subscriptionTopics = subscriptionTopics

if (process.env.NODE_ENV === 'development') {
  window.WSSubscribtionManager = WSSubscribtionManager
}

export default WSSubscribtionManager
